/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

const dashboardHeadingSx = {
  fontSize: "12px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const dashboardDataSx = {
  fontSize: "10px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

function DefaultInfoCard({
  color = "info",
  icon,
  title,
  description = "",
  value = "",
  compact = false,
  minHeight = 220,
  onClick,
}) {
  const isClickable = typeof onClick === "function";

  return (
    <Card
      onClick={onClick}
      onKeyDown={(event) => {
        if (isClickable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      sx={{
        height: "100%",
        minHeight,
        cursor: isClickable ? "pointer" : "default",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": isClickable
          ? { transform: "translateY(-2px)", boxShadow: "0 12px 24px rgba(52, 71, 103, 0.15)" }
          : undefined,
      }}
    >
      <SoftBox
        pt={compact ? 1.25 : 4}
        pb={compact ? 0.75 : 2}
        px={compact ? 2 : 3}
        display="flex"
        justifyContent="center"
      >
        <SoftBox
          display="grid"
          justifyContent="center"
          alignItems="center"
          bgColor={color}
          color="white"
          width={compact ? "2.5rem" : "4.25rem"}
          height={compact ? "2.5rem" : "4.25rem"}
          shadow="md"
          borderRadius="lg"
          variant="gradient"
        >
          <Icon fontSize="default">{icon}</Icon>
        </SoftBox>
      </SoftBox>
      <SoftBox pb={compact ? 1.25 : 3} px={compact ? 2 : 3} textAlign="center" lineHeight={1.35}>
        <SoftTypography
          variant="button"
          color="text"
          fontWeight="regular"
          textTransform="capitalize"
          sx={dashboardHeadingSx}
        >
          {title}
        </SoftTypography>
        {description && (
          <SoftTypography
            variant="caption"
            color="text"
            fontWeight="regular"
            display="block"
            sx={dashboardDataSx}
          >
            {description}
          </SoftTypography>
        )}
        {description && !value ? null : <Divider />}
        {value && (
          <SoftTypography
            component="div"
            variant="caption"
            fontWeight="regular"
            color="dark"
            sx={dashboardDataSx}
          >
            {value}
          </SoftTypography>
        )}
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the DefaultInfoCard
DefaultInfoCard.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compact: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
};

export default DefaultInfoCard;
